import React, { useEffect, useState, useRef, useContext, useReducer } from 'react';
import './speedViolation.css';

import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';

import zoomPlugin from 'chartjs-plugin-zoom';
import ItemList from '../../components/sections/itemList';
import DateRangeSelector from '../../components/sections/dateRangeSelector';
import Loader from '../../components/loader';
import { endOfDay, filterSpeedViolationReport, formatDate, formatDateRange, startOfDay, subtractDays } from '../../utils/generalFunctions';
import RetryReport from '../../components/retryReport';
import ChartLegends from '../../components/chartLegends';
import { Box, Tab, Tabs, Tooltip as TooltipMui } from '@mui/material';
import { DateContext } from '../../context/dateProvider';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
);

const SpeedViolation = ({ dataReport, devices, updateDevices, progress, error, fetchReport, userConstants, updateUserConstants, isRecoveredReports }) => {
    const { startDate, endDate, today,
        yesterday,
        startActualWeek,
        endActualWeek,
        startPreviousWeek,
        endPreviousWeek,
        startActualMonth,
        endActualMonth,
        startPreviousMonth,
        endPreviousMonth,
    } = useContext(DateContext);

    const [isLoading, setIsLoading] = useState(true);
    const [speedViolationData, setSpeedViolationData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.speedViolationData : []);
    const [limitedData, setLimitedData] = useState([]);
    const [activeTab, setActiveTab] = useState('bar');
    const [dateRange, setDateRange] = useState(0);
    const [showAllTopHigher, setShowAllTopHigher] = useState(false);
    const [showAllTopLesser, setShowAllTopLesser] = useState(false);
    const [showAllTopMaxSpeed, setShowAllTopMaxSpeed] = useState(false);
    const [dateTopHigherRange, setDateTopHigherRange] = useState(0);
    const [dateTopLesserRange, setDateTopLesserRange] = useState(0);
    const [dateTopMaxSpeedRange, setDateTopMaxSpeedRange] = useState(0);
    const [displayedHigherData, setDisplayedHigherData] = useState([]);
    const [displayedLesserData, setDisplayedLesserData] = useState([]);
    const [duplicatedDisplayedHigherData, setDuplicatedDisplayedHigherData] = useState([]);
    const [duplicatedDisplayedLesserData, setDuplicatedDisplayedLesserData] = useState([]);
    const [displayedMaxSpeedData, setDisplayedMaxSpeedData] = useState([]);
    const [showChartComparison, setShowChartComparison] = useState(false);
    const chartRef = useRef(null);
    const [speedLimit, setSpeedLimit] = useState(userConstants.speedLimit);

    const [duplicatedSpeedLimit, setDuplicatedSpeedLimit] = useState(userConstants.duplicatedSpeedLimit);

    const [duplicateChartCard, setDuplicateChartCard] = useState(false);
    const [duplicatedDevices, setDuplicatedDevices] = useState([]);
    const [duplicatedLimitedData, setDuplicatedLimitedData] = useState([]);
    const duplicatedChartRef = useRef(null);

    const [duplicateHigherTopCard, setDuplicateHigherTopCard] = useState(false);

    const [duplicateLesserTopCard, setDuplicateLesserTopCard] = useState(false);

    // const today = new Date(endDate);
    // const yesterday = subtractDays(1, today);
    // const startActualWeek = subtractDays(6, today);
    // const endActualWeek = today;
    // const startActualMonth = subtractDays(29, today);
    // const endActualMonth = today;
    // const startPreviousWeek = subtractDays(13, today);
    // const endPreviousWeek = subtractDays(7, today, true);
    // const startPreviousMonth = subtractDays(59, today);
    // const endPreviousMonth = subtractDays(30, today, true);

    useEffect(() => {
        setSpeedLimit(userConstants.speedLimit);
    }, [userConstants.speedLimit]);

    useEffect(() => {
        setDuplicatedSpeedLimit(userConstants.duplicatedSpeedLimit);
    }, [userConstants.duplicatedSpeedLimit]);

    useEffect(() => {
        if (devices.length > 0 && duplicatedDevices.length === 0) {
            setDuplicatedDevices(devices);
        }
    }, [devices]);

    useEffect(() => {
        if (!dataReport || dataReport.length === 0) return;
        setIsLoading(true);

        const processedData = filterSpeedViolationReport(dataReport, startPreviousMonth, endDate);

        setSpeedViolationData(processedData);

        updateUserConstants({ "reports.speedViolationData": processedData });

        setIsLoading(false);
    }, [dataReport]);
    // console.log(speedViolationData, limitedData);
    useEffect(() => {
        const dataByLimit = speedViolationData.map(dayData => {
            const filteredDayData = { date: dayData.date };

            Object.keys(dayData).forEach(sheetHeader => {
                if (sheetHeader !== 'date') {
                    // Filtramos las filas que cumplen con el límite de velocidad
                    filteredDayData[sheetHeader] = dayData[sheetHeader].filter(row => row.max_speed >= speedLimit);
                }
            });

            return filteredDayData;
        });

        setLimitedData(dataByLimit);
    }, [speedViolationData, speedLimit]);

    useEffect(() => {
        const dataByLimit = speedViolationData.map(dayData => {
            const filteredDayData = { date: dayData.date };

            Object.keys(dayData).forEach(sheetHeader => {
                if (sheetHeader !== 'date') {
                    // Filtramos las filas que cumplen con el límite de velocidad
                    filteredDayData[sheetHeader] = dayData[sheetHeader].filter(row => row.max_speed >= duplicatedSpeedLimit);
                }
            });

            return filteredDayData;
        });

        setDuplicatedLimitedData(dataByLimit);
    }, [speedViolationData, duplicatedSpeedLimit]);

    useEffect(() => {
        if (speedViolationData.length === 0 || !chartRef.current || !chartRef.current.chartInstance) return;

        const updatedDatasets = devices.map(device => device.label)
            .flatMap((label, index) => {
                const currentData = filteredData.map(day => day[label]?.length || 0);
                const previousData = filteredPreviousData.map(day => day[label]?.length || 0);

                const isVisible = devices.find(device => device.label === label)?.visible ?? true;

                const baseDataset = {
                    label,
                    data: currentData,
                    fill: false,
                    borderColor: vibrantColors[index % vibrantColors.length],
                    backgroundColor: vibrantColors[index % vibrantColors.length],
                    tension: 0.1,
                    borderWidth: 2,
                    hidden: !isVisible // Ocultar según el estado visible del dispositivo
                };

                const comparisonDataset = showChartComparison ? {
                    label: `${label} (Anterior)`,
                    data: previousData,
                    fill: false,
                    borderColor: vibrantColors[index % vibrantColors.length],
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    borderDash: [5, 5],
                    tension: 0.1,
                    borderWidth: 2,
                    hidden: !isVisible // Ocultar también para comparación
                } : null;

                return [baseDataset, comparisonDataset].filter(Boolean);
            });

        if (chartRef.current) {
            chartRef.current.data.datasets = updatedDatasets;
            chartRef.current.update();
        }
    }, [speedViolationData, devices]);

    // const calculateTotal = (speedViolationData) => {
    //         let accumulatedData = {
    //             "Hoy": {},
    //             "Ayer": {},
    //             "Semana actual": {},
    //             "Semana previa": {},
    //             "Mes actual": {},
    //             "Mes previo": {}
    //         };

    //         const addDataToPeriod = (period, carName, data) => {
    //             if (!accumulatedData[period][carName]) {
    //                 accumulatedData[period][carName] = {
    //                     ignition_on: 0,
    //                     idle_duration: 0,
    //                     ignition_on_percent: 0,
    //                 };
    //             }

    //             accumulatedData[period][carName].ignition_on += Number(data.ignition_on || 0);
    //             accumulatedData[period][carName].idle_duration += Number(data.idle_duration || 0);
    //             accumulatedData[period][carName].ignition_on_percent += Number(data.ignition_on_percent || 0);
    //         };

    //         engineData.forEach(obj => {
    //             const entryDate = new Date(obj.date);

    //             Object.keys(obj).forEach(carName => {
    //                 if (carName !== 'date') {
    //                     const data = obj[carName] || { ignition_on: 0, idle_duration: 0, ignition_on_percent: 0 };

    //                     if (entryDate.toDateString() === today.toDateString()) {
    //                         addDataToPeriod("Hoy", carName, data);
    //                     } else if (entryDate.toDateString() === yesterday.toDateString()) {
    //                         addDataToPeriod("Ayer", carName, data);
    //                     }

    //                     if (entryDate >= startActualWeek && entryDate <= endActualWeek) {
    //                         addDataToPeriod("Semana actual", carName, data);
    //                     }

    //                     if (entryDate >= startActualMonth && entryDate <= endActualMonth) {
    //                         addDataToPeriod("Mes actual", carName, data);
    //                     }

    //                     if (entryDate >= startPreviousWeek && entryDate <= endPreviousWeek) {
    //                         addDataToPeriod("Semana previa", carName, data);
    //                     }

    //                     if (entryDate >= startPreviousMonth && entryDate <= endPreviousMonth) {
    //                         addDataToPeriod("Mes previo", carName, data);
    //                     }
    //                 }
    //             });
    //         });

    //         return accumulatedData;
    //     };

    //     useEffect(() => {
    //         const newTotalData = calculateTotal(speedViolationData);
    //         setSpeedViolationData(newTotalData);
    //     }, [today, yesterday, startActualWeek, endActualWeek, startPreviousWeek, endPreviousWeek]);

    const filterDataByRange = (range, isPrevious = false, isDuplicated = false) => {
        const filteredData = [];

        let startDate2, endDate2;

        // Elegir fechas según el rango y si es el periodo anterior o actual
        switch (range) {
            case 0:
                startDate2 = isPrevious ? startOfDay(yesterday) : startOfDay(today);
                endDate2 = isPrevious ? endOfDay(yesterday) : endOfDay(today);
                break;
            case 1:
                startDate2 = isPrevious ? startOfDay(startPreviousWeek) : startOfDay(startActualWeek);
                endDate2 = isPrevious ? endOfDay(endPreviousWeek) : startOfDay(endActualWeek);
                break;
            case 2:
                startDate2 = isPrevious ? startOfDay(startPreviousMonth) : startOfDay(startActualMonth);
                endDate2 = isPrevious ? endOfDay(endPreviousMonth) : endOfDay(endActualMonth);
                break;
            default:
                startDate2 = startOfDay(new Date(startDate));
                endDate2 = endOfDay(today);
        }

        if (isDuplicated) {
            // Filtrar los datos de acuerdo al rango seleccionado
            duplicatedLimitedData.forEach(item => {
                if (new Date(item.date) >= startDate2 && new Date(item.date) <= endDate2) {
                    filteredData.push(item);
                }
            });
        } else {
            // Filtrar los datos de acuerdo al rango seleccionado
            limitedData.forEach(item => {
                if (new Date(item.date) >= startDate2 && new Date(item.date) <= endDate2) {
                    filteredData.push(item);
                }
            });
        }

        return filteredData;
    };

    // Uso de la nueva función generalizada
    const filteredData = filterDataByRange(dateRange);               // Para el rango actual
    const filteredPreviousData = filterDataByRange(dateRange, true); // Para el rango anterior

    const duplicatedFilteredData = filterDataByRange(dateRange, false, true);               // Para el rango actual
    const duplicatedFilteredPreviousData = filterDataByRange(dateRange, true, true); // Para el rango anterior

    const validDates = filteredData.filter(day =>
        devices.some(device => device.visible && Array.isArray(day[device.label]) && day[device.label].length > 0)
    );

    const labels = validDates.map(day => formatDate(new Date(day.date)));

    const duplicatedValidDates = duplicatedFilteredData.filter(day =>
        devices.some(device => device.visible && Array.isArray(day[device.label]) && day[device.label].length > 0)
    );

    const duplicatedLabels = duplicatedValidDates.map(day => formatDate(new Date(day.date)));

    // const duplicatedLabels = duplicatedFilteredData.map(item => formatDate(new Date(item.date)));

    const vibrantColors = [];
    devices.forEach(item => {
        vibrantColors.push(item.color);
    });

    const getDatasets = ((showChartComparison, filteredData, filteredPreviousData, isDuplicated = false) => {
        const activeDevices = isDuplicated ? duplicatedDevices : devices;

        const validDates = filteredData.filter(day =>
            activeDevices.some(device => device.visible && Array.isArray(day[device.label]) && day[device.label].length > 0)
        );

        const validPreviousDates = filteredPreviousData.filter(day =>
            activeDevices.some(device => device.visible && Array.isArray(day[device.label]) && day[device.label].length > 0)
        );

        return activeDevices.map(device => device.label)
            .flatMap((label, index) => {
                const currentData = validDates.map(day =>
                    Array.isArray(day[label]) ? day[label].length : 0
                );

                const previousData = validPreviousDates.map(day =>
                    Array.isArray(day[label]) ? day[label].length : 0
                );

                if (!isDuplicated) {
                    const isVisible = activeDevices.find(device => device.label === label)?.visible ?? true;

                    return [
                        {
                            label: `${label}`,
                            data: currentData,
                            fill: false,
                            borderColor: vibrantColors[index % vibrantColors.length],
                            backgroundColor: vibrantColors[index % vibrantColors.length],
                            tension: 0.1,
                            borderWidth: 2,
                            hidden: !isVisible // Ocultar según el estado visible del dispositivo
                        },
                        showChartComparison && {
                            label: `${label} (Anterior)`,
                            data: previousData,
                            fill: false,
                            borderColor: vibrantColors[index % vibrantColors.length],
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                            borderDash: [5, 5],
                            tension: 0.1,
                            borderWidth: 2,
                            hidden: !isVisible // Ocultar también para comparación
                        }
                    ].filter(Boolean);
                } else {
                    const isVisible = activeDevices.find(device => device.label === label)?.visible ?? true;
                    return [
                        {
                            label: `${label}`,
                            data: currentData,
                            fill: false,
                            borderColor: vibrantColors[index % vibrantColors.length],
                            backgroundColor: vibrantColors[index % vibrantColors.length],
                            tension: 0.1,
                            borderWidth: 2,
                            hidden: !isVisible // Ocultar también para comparación
                        },
                        showChartComparison && {
                            label: `${label} (Anterior)`,
                            data: previousData,
                            fill: false,
                            borderColor: vibrantColors[index % vibrantColors.length],
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                            borderDash: [5, 5],
                            tension: 0.1,
                            borderWidth: 2,
                            hidden: !isVisible // Ocultar también para comparación
                        }
                    ].filter(Boolean);
                }
            }) || [];
    });

    const datasets = getDatasets(showChartComparison, filteredData, filteredPreviousData);
    const data = {
        labels,
        datasets,
    };

    const duplicatedDatasets = getDatasets(showChartComparison, duplicatedFilteredData, duplicatedFilteredPreviousData, true);
    const duplicatedData = {
        labels: duplicatedLabels,
        datasets: duplicatedDatasets,
    };

    const getOptions = ((dateRange, showChartComparison, filteredData, filteredPreviousData, isDuplicated = false) => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                intersect: false,
            },
            scales: {
                x: {
                    display: true,
                    title: {
                        display: false,
                        text: "Fecha",
                        font: {
                            size: 16,
                            weight: 'bold',
                            lineHeight: 1.2,
                        },
                    }
                },
                y: {
                    display: true,
                    title: {
                        display: true,
                        text: "Excesos de velocidad",
                        font: {
                            size: 16,
                            weight: 'bold',
                            lineHeight: 1.2,
                        },
                    }
                }
            },
            plugins: {
                zoom: {
                    zoom: {
                        wheel: {
                            enabled: dateRange !== 0, // Habilitar zoom con la rueda del ratón
                            threshold: 5,
                            modifierKey: 'ctrl'
                        },
                        pinch: {
                            enabled: dateRange !== 0, // Habilitar zoom con gestos táctiles
                            threshold: 2,
                        },
                        mode: 'x', // Hacer zoom solo en el eje X
                    },
                    pan: {
                        enabled: true, // Habilitar paneo (desplazamiento)
                        mode: 'x', // Permitir desplazamiento solo en el eje X
                        modifierKey: 'ctrl'
                    },
                },
                legend: {
                    display: false,
                    position: 'bottom',
                    onHover: (event) => {
                        event.native.target.style.cursor = 'pointer';
                    },
                    onLeave: (event) => {
                        event.native.target.style.cursor = 'default';
                    },
                    onClick: (e, legendItem, legend) => {
                        const chart = legend.chart;
                        const datasetIndex = legendItem.datasetIndex;
                        const dataset = chart.data.datasets[datasetIndex];
                        const deviceName = dataset.label.replace(' (Anterior)', '');

                        // Alternar visibilidad del dataset
                        const isHidden = !dataset.hidden;
                        dataset.hidden = isHidden;

                        if (!isDuplicated) {
                            // Actualiza el estado de `devices` para reflejar el cambio
                            const updatedDevices = devices.map(device =>
                                device.label === deviceName ? { ...device, visible: !isHidden } : device
                            );

                            updateDevices(updatedDevices);
                        } else {
                            setDuplicatedDevices(duplicatedDevices.map(device => {
                                return device.label === deviceName ? { ...device, visible: !device.visible } : device;
                            }));
                        }

                        // Si `showChartComparison` está activado, también alterna el dataset de comparación
                        if (showChartComparison) {
                            const comparisonDataset = chart.data.datasets[datasetIndex + 1];
                            if (comparisonDataset && comparisonDataset.label.includes('(Anterior)')) {
                                comparisonDataset.hidden = isHidden;
                            }
                        }

                        chart.update(); // Actualiza la gráfica
                    },

                    labels: {
                        usePointStyle: true,
                        padding: 15,
                        pointStyle: 'circle',
                        boxWidth: 8,        // Ajusta el ancho de la caja del punto
                        boxHeight: 8,
                        font: {
                            size: 14
                        },
                        filter: (legendItem) => !legendItem.text.includes('Anterior'), // Filtra para mostrar solo "Actual"
                    },
                },
                title: {
                    display: false,
                    text: 'Excesos de velocidad por flota',
                },
                tooltip: {
                    callbacks: {
                        title: function (tooltipItems) {
                            // Solo tomamos el primer elemento del array tooltipItems
                            const tooltipItem = tooltipItems[0];
                            const index = tooltipItem.dataIndex;
                            const isPreviousPeriod = tooltipItem.dataset.label.includes('(Anterior)');
                            const previousDates = filteredPreviousData.map(item => formatDate(new Date(item.date)));

                            // Obtener la fecha correspondiente
                            const dateLabel = isPreviousPeriod
                                ? previousDates[index] + ' (Previo)' // Si es el periodo anterior, usa `previousDates`
                                : labels[index];       // Si es el actual, usa `labels`

                            return dateLabel;
                        },
                        label: function (tooltipItem) {
                            const isPreviousPeriod = tooltipItem.dataset.label.includes('(Anterior)');
                            const datasetLabel = tooltipItem.dataset.label.replace(/\s*\(Actual\)|\s*\(Anterior\)/g, '');
                            const dataIndex = tooltipItem.dataIndex;

                            // Determina si se utiliza el período actual o anterior
                            const carData = isPreviousPeriod
                                ? filteredPreviousData[dataIndex][datasetLabel]
                                : filteredData[dataIndex][datasetLabel];

                            // Verifica si `carData` es un array
                            if (!Array.isArray(carData)) {
                                return `${datasetLabel} - Excesos: 0`; // Regresa 0 si no es un array
                            }

                            // Cuenta los excesos de velocidad en el array `carData`
                            const count = carData.length;

                            // Formatea el texto del tooltip
                            let labelText = `${datasetLabel} - Excesos: ${count}`;

                            // Llamamos a `setValuesPeriod` con el nombre del dataset
                            setValuesPeriod(datasetLabel, dateRange, filteredData, filteredPreviousData, isDuplicated);

                            return labelText;
                        }
                    }
                }
            },
        };
    });

    const options = getOptions(dateRange, showChartComparison, filteredData, filteredPreviousData);
    const optionsDuplicated = getOptions(dateRange, showChartComparison, duplicatedFilteredData, duplicatedFilteredPreviousData, true);

    const handleCheckboxChange = () => {
        setShowChartComparison(!showChartComparison); // Alternar el estado
    };

    const handleChangeSpeedLimit = (event) => {
        const value = Number(event.target.value);
        updateUserConstants({ speedLimit: value });
        setSpeedLimit(value);
    };

    const handleDuplicatedChangeSpeedLimit = (event) => {
        const value = Number(event.target.value);
        updateUserConstants({ duplicatedSpeedLimit: value });
        setDuplicatedSpeedLimit(value);
    };

    const calculatePeriodTotalData = (data, car) => {
        if (data && Array.isArray(data)) {
            return data.reduce((count, day) => {
                if (day[car] && Array.isArray(day[car])) { // Verificamos que day[car] sea un array
                    count += day[car].length;
                }
                return count;
            }, 0);
        }

        return 0; // Si no hay datos, retorna 0
    };

    const setValuesPeriod = (dataset, dateRange, filteredData, filteredPreviousData, isDuplicated = false) => {
        const selectedOption = dateRange;
        let currentPeriodSpeedViolation, previousPeriodSpeedViolation, currentPeriodDates, previousPeriodDates;

        // Ajustamos los datos y las fechas según la opción seleccionada
        if (selectedOption === 0) {
            currentPeriodSpeedViolation = calculatePeriodTotalData(filteredData, dataset);
            previousPeriodSpeedViolation = calculatePeriodTotalData(filteredPreviousData, dataset);

            currentPeriodDates = { startDate: startOfDay(new Date(today)), endDate: today };
            previousPeriodDates = { startDate: yesterday, endDate: subtractDays(1, today, true) };
        } else if (selectedOption === 1) {
            currentPeriodSpeedViolation = calculatePeriodTotalData(filteredData, dataset);
            previousPeriodSpeedViolation = calculatePeriodTotalData(filteredPreviousData, dataset);

            currentPeriodDates = { startDate: startActualWeek, endDate: endActualWeek };
            previousPeriodDates = { startDate: startPreviousWeek, endDate: endPreviousWeek };
        } else if (selectedOption === 2) {
            currentPeriodSpeedViolation = calculatePeriodTotalData(filteredData, dataset);
            previousPeriodSpeedViolation = calculatePeriodTotalData(filteredPreviousData, dataset);

            currentPeriodDates = { startDate: startActualMonth, endDate: endActualMonth };
            previousPeriodDates = { startDate: startPreviousMonth, endDate: endPreviousMonth };
        }

        const currentPeriodElem = isDuplicated ? document.getElementById('duplicated-current-period-speed_violation') : document.getElementById('current-period-speed_violation');
        const previousPeriodElem = isDuplicated ? document.getElementById('duplicated-previous-period-speed_violation') : document.getElementById('previous-period-speed_violation');

        currentPeriodElem.innerText = `${currentPeriodSpeedViolation} excesos`;
        previousPeriodElem.innerText = `${previousPeriodSpeedViolation} excesos`;

        // Remover clases previas
        currentPeriodElem.classList.remove('high', 'normal', 'low');
        previousPeriodElem.classList.remove('high', 'normal', 'low');

        // Cambiar el className según la comparación
        if (currentPeriodSpeedViolation > previousPeriodSpeedViolation) {
            currentPeriodElem.classList.add('high');
            previousPeriodElem.classList.add('low');
        } else if (currentPeriodSpeedViolation < previousPeriodSpeedViolation) {
            currentPeriodElem.classList.add('low');
            previousPeriodElem.classList.add('high');
        } else {
            currentPeriodElem.classList.add('normal');
            previousPeriodElem.classList.add('normal');
        }

        const currentPeriodDateElem = isDuplicated ? document.getElementById('duplicated-current-period-date-speed_violation') : document.getElementById('current-period-date-speed_violation');
        const previousPeriodDateElem = isDuplicated ? document.getElementById('duplicated-previous-period-date-speed_violation') : document.getElementById('previous-period-date-speed_violation');


        // Formatear las fechas en dd/mm/aaaa - dd/mm/aaaa
        if (selectedOption === 0) {
            currentPeriodDateElem.innerText = `(${formatDate(currentPeriodDates.startDate)})`;
            previousPeriodDateElem.innerText = `(${formatDate(previousPeriodDates.startDate)})`;
        } else {
            currentPeriodDateElem.innerText = `(${formatDate(currentPeriodDates.startDate)} - ${formatDate(currentPeriodDates.endDate)})`;
            previousPeriodDateElem.innerText = `(${formatDate(previousPeriodDates.startDate)} - ${formatDate(previousPeriodDates.endDate)})`;
        }
    };

    const resetZoom = (chartRef) => {
        if (chartRef.current) {
            chartRef.current.resetZoom(); // Usar la referencia para resetear el zoom
        }
    };

    const getSpeedViolationByDateRange = (range, isMaxSpeed = false, limitedData, devices) => {
        let periodCurrentData = [], periodPreviousData = [];
        let combinedData = [];

        switch (range) {
            case 0:
                periodCurrentData = limitedData.filter(item => new Date(item.date).toDateString() === today.toDateString());
                periodPreviousData = limitedData.filter(item => new Date(item.date).toDateString() === yesterday.toDateString());
                break;
            case 1:
                periodCurrentData = limitedData.filter(item =>
                    new Date(item.date) >= startActualWeek && new Date(item.date) <= endActualWeek
                );
                periodPreviousData = limitedData.filter(item =>
                    new Date(item.date) >= startPreviousWeek && new Date(item.date) <= endPreviousWeek
                );
                break;
            case 2:
                periodCurrentData = limitedData.filter(item =>
                    new Date(item.date) >= startActualMonth && new Date(item.date) <= endActualMonth
                );
                periodPreviousData = limitedData.filter(item =>
                    new Date(item.date) >= startPreviousMonth && new Date(item.date) <= endPreviousMonth
                );
                break;
            default:
                return [];
        }

        // Filtramos dispositivos visibles
        const visibleDevices = devices.filter(device => device.visible);

        // Combinar datos del periodo actual y el periodo anterior
        const allCars = new Set([
            ...periodCurrentData.flatMap(item => Object.keys(item).filter(key => key !== 'date')),
            ...periodPreviousData.flatMap(item => Object.keys(item).filter(key => key !== 'date'))
        ]);

        if (isMaxSpeed) {
            combinedData = Array.from(allCars)
                .filter(car => visibleDevices.find(device => device.label === car)) // Filtra solo los visibles
                .map((car) => {
                    const carData = { duration: 0, start_time: 0, avg_speed: 0, max_speed: 0 }
                    // Obtener los datos actuales y anteriores para cada vehículo
                    const currentCarData = periodCurrentData.flatMap(day => day[car] || []);
                    const previousCarData = periodPreviousData.flatMap(day => day[car] || []);

                    // Encontrar el registro de máxima velocidad en el periodo actual
                    const maxCurrentData = currentCarData.reduce((maxData, row) => {
                        return row.max_speed > (maxData?.max_speed || 0) ? row : maxData;
                    }, null);

                    // Encontrar el registro de máxima velocidad en el periodo anterior
                    const maxPreviousData = previousCarData.reduce((maxData, row) => {
                        return row.max_speed > (maxData?.max_speed || 0) ? row : maxData;
                    }, null);

                    return {
                        car,
                        currentData: maxCurrentData ? {
                            duration: maxCurrentData.duration,
                            start_time: maxCurrentData.start_time,
                            avg_speed: maxCurrentData.avg_speed,
                            max_speed: maxCurrentData.max_speed
                        } : carData,
                        previousData: maxPreviousData ? {
                            duration: maxPreviousData.duration,
                            start_time: maxPreviousData.start_time,
                            avg_speed: maxPreviousData.avg_speed,
                            max_speed: maxPreviousData.max_speed
                        } : carData,
                    };
                });
        } else {
            combinedData = Array.from(allCars)
                .filter(car => visibleDevices.find(device => device.label === car)) // Filtra solo los visibles
                .map((car) => {
                    // Contar excesos de velocidad en el periodo actual
                    const currentViolations = periodCurrentData.reduce((total, day) => {
                        return total + (day[car] ? day[car].length : 0);
                    }, 0);

                    // Contar excesos de velocidad en el periodo anterior
                    const previousViolations = periodPreviousData.reduce((total, day) => {
                        return total + (day[car] ? day[car].length : 0);
                    }, 0);

                    return {
                        car, // Nombre del vehículo
                        currentViolations, // Excesos de velocidad actuales
                        previousViolations // Excesos de velocidad previos
                    };
                });
        }

        // Ordenar por el número de excesos de velocidad actuales
        return combinedData;
    };

    // Ordenar para obtener los vehículos con más excesos de velocidad
    useEffect(() => {
        const sortedHigherViolationData = getSpeedViolationByDateRange(dateTopHigherRange, false, limitedData, devices)
            .sort((a, b) => {
                if (b.currentViolations === a.currentViolations) {
                    return b.previousViolations - a.previousViolations; // Comparar por excesos previos si los actuales son iguales
                }
                return b.currentViolations - a.currentViolations; // Ordenar por excesos actuales de forma descendente
            });
        setDisplayedHigherData(showAllTopHigher ? sortedHigherViolationData : sortedHigherViolationData.slice(0, 5));
    }, [devices, dateTopHigherRange, limitedData, showAllTopHigher]);

    // Ordenar para obtener los vehículos con más excesos de velocidad
    useEffect(() => {
        const duplicatedSortedHigherViolationData = getSpeedViolationByDateRange(dateTopHigherRange, false, duplicatedLimitedData, duplicatedDevices)
            .sort((a, b) => {
                if (b.currentViolations === a.currentViolations) {
                    return b.previousViolations - a.previousViolations; // Comparar por excesos previos si los actuales son iguales
                }
                return b.currentViolations - a.currentViolations; // Ordenar por excesos actuales de forma descendente
            });
        setDuplicatedDisplayedHigherData(showAllTopHigher ? duplicatedSortedHigherViolationData : duplicatedSortedHigherViolationData.slice(0, 5));
    }, [duplicatedDevices, dateTopHigherRange, duplicatedLimitedData, showAllTopHigher]);

    // Ordenar para obtener los vehículos con menos excesos de velocidad
    useEffect(() => {
        const sortedLesserViolationData = getSpeedViolationByDateRange(dateTopLesserRange, false, limitedData, devices)
            .sort((a, b) => {
                if (a.currentViolations === b.currentViolations) {
                    if (a.previousViolations === b.previousViolations) {
                        return -1;
                    }
                    return a.previousViolations - b.previousViolations; // Comparar por excesos previos si los actuales son iguales
                }
                return a.currentViolations - b.currentViolations; // Ordenar por excesos actuales de forma ascendente
            });
        setDisplayedLesserData(showAllTopLesser ? sortedLesserViolationData : sortedLesserViolationData.slice(0, 5));
    }, [devices, dateTopLesserRange, limitedData, showAllTopLesser]);

    // Ordenar para obtener los vehículos con menos excesos de velocidad
    useEffect(() => {
        const duplicatedSortedLesserViolationData = getSpeedViolationByDateRange(dateTopLesserRange, false, duplicatedLimitedData, duplicatedDevices)
            .sort((a, b) => {
                if (a.currentViolations === b.currentViolations) {
                    if (a.previousViolations === b.previousViolations) {
                        return -1;
                    }
                    return a.previousViolations - b.previousViolations; // Comparar por excesos previos si los actuales son iguales
                }
                return a.currentViolations - b.currentViolations; // Ordenar por excesos actuales de forma ascendente
            });
        setDuplicatedDisplayedLesserData(showAllTopLesser ? duplicatedSortedLesserViolationData : duplicatedSortedLesserViolationData.slice(0, 5));
    }, [duplicatedDevices, dateTopLesserRange, duplicatedLimitedData, showAllTopLesser]);

    useEffect(() => {
        const sortedMaxSpeedData = getSpeedViolationByDateRange(dateTopMaxSpeedRange, true, limitedData, devices)
            .sort((a, b) => {
                if (b.currentData.max_speed === a.currentData.max_speed) {
                    return b.previousData.max_speed - a.previousData.max_speed;
                }
                return b.currentData.max_speed - a.currentData.max_speed;
            });
        setDisplayedMaxSpeedData(showAllTopMaxSpeed ? sortedMaxSpeedData : sortedMaxSpeedData.slice(0, 5));
    }, [devices, dateTopMaxSpeedRange, limitedData, showAllTopMaxSpeed]);

    const totalItems = displayedHigherData.length;
    // console.log(displayedHigherData);

    // Calcular el ancho basado en la cantidad de dígitos
    const indexWidth = totalItems < 10 ? '2ch' : totalItems < 100 ? '3ch' : '4ch';

    // Función para alternar entre "Ver todos" y "Ver menos"
    const handleShowAllTopHigherToggle = () => {
        setShowAllTopHigher(!showAllTopHigher);
    };

    const handleShowAllTopLesserToggle = () => {
        setShowAllTopLesser(!showAllTopLesser);
    };

    const handleShowAllTopMaxSpeedToggle = () => {
        setShowAllTopMaxSpeed(!showAllTopMaxSpeed);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const showZoomMessage = useRef(false);
    const [, forceRender] = useReducer(x => x + 1, 0);
    const zoomTimeout = useRef(null);


    const handleWheel = (e) => {
        if (dateRange > 0 && !e.ctrlKey) {
            if (!showZoomMessage.current) {
                showZoomMessage.current = true;
                forceRender();

                if (zoomTimeout.current) clearTimeout(zoomTimeout.current);
                zoomTimeout.current = setTimeout(() => {
                    showZoomMessage.current = false;
                    forceRender();
                }, 3000);
            }
        }
    };

    const renderContent = (isDuplicated) => {
        switch (activeTab) {
            case 'bar':
                if (isDuplicated) {

                    return <>
                        <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
                            <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                                Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                            </div>
                            <Bar ref={duplicatedChartRef} data={duplicatedData} options={optionsDuplicated} />
                        </div>
                        <ChartLegends devices={duplicatedDevices} updateDevices={setDuplicatedDevices} id={'speedViolation-duplicatedBar-'} />
                    </>
                } else {
                    return <>
                        <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
                            <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                                Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                            </div>
                            <Bar ref={chartRef} data={data} options={options} />
                        </div>
                        <ChartLegends devices={devices} updateDevices={updateDevices} id={'speedViolation-bar-'} />
                    </>
                }
            case 'line':
                if (isDuplicated) {
                    return <>
                        <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
                            <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                                Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                            </div>
                            <Line ref={duplicatedChartRef} data={duplicatedData} options={optionsDuplicated} />
                        </div >
                        <ChartLegends devices={duplicatedDevices} updateDevices={setDuplicatedDevices} id={'speedViolation-duplicatedLine-'} />
                    </>
                } else {
                    return <>
                        <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
                            <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                                Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                            </div>
                            <Line ref={chartRef} data={data} options={options} />
                        </div>
                        <ChartLegends devices={devices} updateDevices={updateDevices} id={'speedViolation-line-'} />
                    </>
                }
            case 'table':
                if (isDuplicated) {
                    return renderTable();
                } else {
                    return renderTable();
                }
            default:
                return null;
        }
    };

    const toggleAllDevices = () => {
        const allVisible = duplicatedDevices.every(device => device.visible);
        const updatedDevices = duplicatedDevices.map(device => ({ ...device, visible: !allVisible }));
        setDuplicatedDevices(updatedDevices);
    };

    const getCheckboxState = () => {
        const allVisible = duplicatedDevices.every(device => device.visible);
        const noneVisible = duplicatedDevices.every(device => !device.visible);

        if (allVisible) return true;
        if (noneVisible) return false;
        return 'indeterminate';
    };

    const renderTable = () => {
        // const visibleDevices = devices.filter(device => device.visible).map(device => device.label);

        // const summary = {};

        // const actualData = filterDataByRange(dateRange); // Datos del periodo actual
        // const previousData = filterDataByRange(dateRange, true); // Datos del periodo anterior

        // const dates = [];
        // const previousDates = [];

        // // Filtrar los datos del periodo actual para llenar el resumen solo con los dispositivos visibles
        // actualData.forEach(entry => {
        //     const { date, ...cars } = entry;
        //     if (!dates.includes(date)) dates.push(date); // Agregar fechas del periodo actual sin duplicarlas

        //     visibleDevices.forEach(carName => {
        //         if (cars[carName]) {
        //             if (!summary[carName]) {
        //                 summary[carName] = { actual: [], previous: [], totalActualMileage: 0, totalActualTime: 0, totalPreviousMileage: 0, totalPreviousTime: 0 };
        //             }
        //             const carData = cars[carName];
        //             summary[carName].actual.push({
        //                 mileage: carData.mileage,
        //                 time: carData.time
        //             });
        //             summary[carName].totalActualMileage += carData.mileage;
        //             summary[carName].totalActualTime += carData.time;
        //         }
        //     });
        // });

        // // Filtrar los datos del periodo anterior para llenar el resumen solo con los dispositivos visibles
        // previousData.forEach(entry => {
        //     const { date, ...cars } = entry;
        //     if (!previousDates.includes(date)) previousDates.push(date); // Agregar fechas del periodo anterior sin duplicarlas

        //     visibleDevices.forEach(carName => {
        //         if (cars[carName]) {
        //             if (!summary[carName]) {
        //                 summary[carName] = { actual: [], previous: [], totalActualMileage: 0, totalActualTime: 0, totalPreviousMileage: 0, totalPreviousTime: 0 };
        //             }
        //             const carData = cars[carName];
        //             summary[carName].previous.push({
        //                 mileage: carData.mileage,
        //                 time: carData.time
        //             });
        //             summary[carName].totalPreviousMileage += carData.mileage;
        //             summary[carName].totalPreviousTime += carData.time;
        //         }
        //     });
        // });

        // return (
        //     <div className='table_container'>
        //         <table>
        //             <thead>
        //                 <tr>
        //                     <th rowSpan={2}>Nombre del Carro</th>
        //                     <th colSpan={2}>Total Periodo Actual</th>
        //                     {showChartComparison && <th colSpan={2} className='previous_data'>Total Periodo Anterior</th>}
        //                     {dates.map((date, index) => (
        //                         <React.Fragment key={`period-${date}-${index}`}>
        //                             <th colSpan={2}>{date}</th>
        //                             {showChartComparison && (
        //                                 <th colSpan={2} className='previous_data'>{previousDates[index] || 'Fecha Anterior'}</th>
        //                             )}
        //                         </React.Fragment>
        //                     ))}
        //                 </tr>
        //                 <tr>
        //                     <th>Kilometraje</th>
        //                     <th>Tiempo</th>
        //                     {showChartComparison && (
        //                         <>
        //                             <th className='previous_data'>Kilometraje</th>
        //                             <th className='previous_data'>Tiempo</th>
        //                         </>
        //                     )}
        //                     {dates.map((date, index) => (
        //                         <React.Fragment key={`subheader-${date}-${index}`}>
        //                             <th key={`current-km-${index}`}>Kilometraje</th>
        //                             <th key={`current-time-${index}`}>Tiempo</th>
        //                             {showChartComparison && (
        //                                 <>
        //                                     <th className='previous_data' key={`previous-km-${index}`}>Kilometraje</th>
        //                                     <th className='previous_data' key={`previous-time-${index}`}>Tiempo</th>
        //                                 </>
        //                             )}
        //                         </React.Fragment>
        //                     ))}
        //                 </tr>
        //             </thead>

        //             <tbody>
        //                 {Object.entries(summary).map(([carName, stats], index) => (
        //                     <tr onMouseOver={() => setValuesPeriod(carName)} key={`car-row-${index}`}>
        //                         <td>{carName}</td>
        //                         <td className="total-cell" key={`total-currentMileage-${carName}`}>
        //                             {stats.totalActualMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
        //                         </td>
        //                         <td className="total-cell" key={`total-currentTime-${carName}`}>
        //                             {formatTime(stats.totalActualTime)}
        //                         </td>
        //                         {showChartComparison && (
        //                             <>
        //                                 <td className="total-cell" key={`total-previousMileage-${carName}`}>
        //                                     {stats.totalPreviousMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
        //                                 </td>
        //                                 <td className="total-cell" key={`total-previousTime-${carName}`}>
        //                                     {formatTime(stats.totalPreviousTime)}
        //                                 </td>
        //                             </>
        //                         )}
        //                         {dates.map((date, dateIndex) => {
        //                             const actualEntry = stats.actual[dateIndex] || { mileage: 0, time: 0 };
        //                             const previousEntry = stats.previous[dateIndex] || { mileage: 0, time: 0 };

        //                             return (
        //                                 <React.Fragment key={`data-${carName}-${date}-${dateIndex}`}>
        //                                     <td key={`currentMileage-${carName}-${dateIndex}`}>
        //                                         {actualEntry.mileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
        //                                     </td>
        //                                     <td key={`currentTime-${carName}-${dateIndex}`}>
        //                                         {formatTime(actualEntry.time)}
        //                                     </td>
        //                                     {showChartComparison && (
        //                                         <>
        //                                             <td key={`previousMileage-${carName}-${dateIndex}`}>
        //                                                 {previousEntry.mileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
        //                                             </td>
        //                                             <td key={`previousTime-${carName}-${dateIndex}`}>
        //                                                 {formatTime(previousEntry.time)}
        //                                             </td>
        //                                         </>
        //                                     )}
        //                                 </React.Fragment>
        //                             );
        //                         })}
        //                     </tr>
        //                 ))}
        //             </tbody>
        //         </table>
        //     </div>
        // );
    };

    const [cardsReady, setCardsReady] = useState([]);

    useEffect(() => {
        const totalCards = 7; // Cambiar este número según la cantidad de tarjetas
        if (progress === 100) {
            // Generar tiempos aleatorios para cada tarjeta
            const randomDelays = Array.from({ length: totalCards }, () => Math.random() * 2000 + 1000); // 1-3 segundos
            const timeouts = randomDelays.map((delay, index) => {
                return setTimeout(() => {
                    setCardsReady((prev) => {
                        const updated = [...prev];
                        updated[index] = true;
                        return updated;
                    });
                }, delay);
            });

            // Limpiar los timeouts al desmontar
            return () => timeouts.forEach((timeout) => clearTimeout(timeout));
        } else {
            setCardsReady(new Array(totalCards).fill(false));
        }
    }, [progress]);


    return (
        <div className='section_grid_container'>
            <div className='dashboard_card card_chart card_large'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={2} />
                    :
                    progress < 100 || !cardsReady[0] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='dashboard_card_header_container'>
                                <span className='dashboard_card_title'>Excesos de velocidad por flota</span>
                                <div className='config_chart_container'>
                                    <DateRangeSelector dateRange={dateRange} setDateRange={setDateRange} />
                                    <div className='switch_container'>
                                        <span>Comparar</span>
                                        <TooltipMui title='Comparar con el periodo previo' arrow slotProps={{
                                            tooltip: {
                                                sx: {
                                                    fontSize: '0.8rem',
                                                    backgroundColor: '#333',
                                                    color: '#fff',
                                                    padding: '8px 15px',
                                                    borderRadius: '8px',
                                                }
                                            }
                                        }}>
                                            <label className="switch">
                                                <input
                                                    type="checkbox"
                                                    checked={showChartComparison}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <span className="slider"></span>
                                            </label>
                                        </TooltipMui>
                                    </div>
                                    <TooltipMui title={duplicateChartCard ? 'Ocultar duplicado' : 'Duplicar tarjeta'} arrow slotProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: '0.8rem',
                                                backgroundColor: '#333',
                                                color: '#fff',
                                                padding: '8px 15px',
                                                borderRadius: '8px',
                                            }
                                        }
                                    }}>
                                        <button className='duplicate_card_btn' onClick={() => { setDuplicateChartCard(!duplicateChartCard) }}>
                                            <span className="material-symbols-outlined">{duplicateChartCard ? 'remove' : 'add'}</span>
                                        </button>
                                    </TooltipMui>
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', marginBottom: '0.5rem' }}>
                                    {/* <div style={{ display: 'flex', gap: '5px' }}>
                                        <button onClick={() => handleTabChange('bar')} className={activeTab === 'bar' ? 'active' : ''}>Gráfica de Barras</button>
                                        <button onClick={() => handleTabChange('line')} className={activeTab === 'line' ? 'active' : ''}>Gráfica de Líneas</button>
                                    </div> */}
                                    <Tabs
                                        value={activeTab}
                                        onChange={(e, newValue) => handleTabChange(newValue)}
                                        textColor="primary"
                                        indicatorColor="primary"
                                        sx={{
                                            minHeight: 0,
                                            '.MuiTab-root': {
                                                textTransform: 'none',
                                                minHeight: 0,
                                                padding: '10px 12px',
                                                fontSize: '0.75rem',
                                            }
                                        }}
                                    >
                                        <Tab label="Gráfica de Barras" value="bar" />
                                        <Tab label="Gráfica de Líneas" value="line" />
                                        {/* <Tab label="Tabla de Datos" value="table" /> */}
                                    </Tabs>
                                    {/* <button onClick={() => handleTabChange('table')} className={activeTab === 'table' ? 'active' : ''}>Tabla de Datos</button> */}
                                    <div className='increment_input_container'>
                                        <label htmlFor="speed_input">Exceso de Velocidad (km/h): </label>
                                        <input
                                            id="speed_input1"
                                            type="number"
                                            value={speedLimit}
                                            min="40"
                                            max="250"
                                            step="5"
                                            onChange={handleChangeSpeedLimit}
                                        />
                                    </div>
                                </Box>
                                <div className='tabs_container'>
                                    {renderContent()}
                                </div>
                                <div className='chart_bottom_container'>
                                    <div className='chart_period_container'>
                                        <div>
                                            <span>Periodo actual: </span>
                                            <span id="current-period-speed_violation">0</span>
                                            <br />
                                            <span className='period_date' id='current-period-date-speed_violation'></span>
                                        </div>
                                        <div>
                                            <span>Periodo anterior: </span>
                                            <span id="previous-period-speed_violation">0</span>
                                            <br />
                                            <span className='period_date' id='previous-period-date-speed_violation'></span>
                                        </div>
                                    </div>
                                    <button className='restart_zoom_btn' onClick={() => resetZoom(chartRef)}>Restablecer Zoom</button>
                                </div>
                            </div>
                        </>
                    )}
            </div>
            {
                duplicateChartCard ? (
                    <div className='dashboard_card card_chart card_large'>
                        {error ?
                            <RetryReport fetchReport={fetchReport} sectionNum={2} />
                            :
                            progress < 100 || !cardsReady[1] ? (
                                <Loader progress={progress} />
                            ) : (
                                <>
                                    <div className='dashboard_card_header_container'>
                                        <span className='dashboard_card_title'>Excesos de velocidad por flota <span className='text_duplicated'>(duplicada)</span></span>
                                        <div className='config_chart_container'>
                                            <div className="checkbox-container">
                                                <label className="custom-checkbox">
                                                    <span className='checkbox_text'>{getCheckboxState() === true ? 'Todos' : getCheckboxState() === false ? 'Ninguno' : 'Varios'}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={getCheckboxState() === true}
                                                        ref={input => input && (input.indeterminate = getCheckboxState() === 'indeterminate')}
                                                        onChange={toggleAllDevices}
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                            </div>
                                            <DateRangeSelector dateRange={dateRange} setDateRange={setDateRange} />
                                            <div className='switch_container'>
                                                <span>Comparar</span>
                                                <TooltipMui title='Comparar con el periodo previo' arrow slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            fontSize: '0.8rem',
                                                            backgroundColor: '#333',
                                                            color: '#fff',
                                                            padding: '8px 15px',
                                                            borderRadius: '8px',
                                                        }
                                                    }
                                                }}>
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={showChartComparison}
                                                            onChange={() => { setShowChartComparison(!showChartComparison) }}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </TooltipMui>
                                            </div>
                                            <TooltipMui title={duplicateChartCard ? 'Ocultar duplicado' : 'Duplicar tarjeta'} arrow slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: '0.8rem',
                                                        backgroundColor: '#333',
                                                        color: '#fff',
                                                        padding: '8px 15px',
                                                        borderRadius: '8px',
                                                    }
                                                }
                                            }}>
                                                <button className='duplicate_card_btn' onClick={() => { setDuplicateChartCard(!duplicateChartCard) }} >
                                                    <span className="material-symbols-outlined">remove</span>
                                                </button>
                                            </TooltipMui>
                                        </div>
                                    </div>
                                    <div className='dashboard_card_content_container'>
                                        <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', marginBottom: '0.5rem' }}>
                                            <Tabs
                                                value={activeTab}
                                                onChange={(e, newValue) => handleTabChange(newValue)}
                                                textColor="primary"
                                                indicatorColor="primary"
                                                sx={{
                                                    minHeight: 0,
                                                    '.MuiTab-root': {
                                                        textTransform: 'none',
                                                        minHeight: 0,
                                                        padding: '10px 12px',
                                                        fontSize: '0.75rem',
                                                    }
                                                }}
                                            >
                                                <Tab label="Gráfica de Barras" value="bar" />
                                                <Tab label="Gráfica de Líneas" value="line" />
                                                {/* <Tab label="Tabla de Datos" value="table" /> */}
                                            </Tabs>
                                            {/* <button onClick={() => handleTabChange('table')} className={activeTab === 'table' ? 'active' : ''}>Tabla de Datos</button> */}
                                            <div className='increment_input_container'>
                                                <label htmlFor="speed_input">Exceso de Velocidad (km/h): </label>
                                                <input
                                                    id="speed_input2"
                                                    type="number"
                                                    value={duplicatedSpeedLimit}
                                                    min="40"
                                                    max="250"
                                                    step="5"
                                                    onChange={handleDuplicatedChangeSpeedLimit}
                                                />
                                            </div>
                                        </Box>
                                        <div className='tabs_container'>
                                            {renderContent(true)}
                                        </div>
                                        <div className='chart_bottom_container'>
                                            <div className='chart_period_container'>
                                                <div>
                                                    <span>Periodo actual: </span>
                                                    <span id="duplicated-current-period-speed_violation">0</span>
                                                    <br />
                                                    <span className='period_date' id='duplicated-current-period-date-speed_violation'></span>
                                                </div>
                                                <div>
                                                    <span>Periodo anterior: </span>
                                                    <span id="duplicated-previous-period-speed_violation">0</span>
                                                    <br />
                                                    <span className='period_date' id='duplicated-previous-period-date-speed_violation'></span>
                                                </div>
                                            </div>
                                            <button className='restart_zoom_btn' onClick={() => resetZoom(duplicatedChartRef)}>Restablecer Zoom</button>
                                        </div>
                                    </div>
                                </>
                            )}
                    </div>
                ) : ''
            }
            <div className='dashboard_card card_top card_short'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={2} />
                    :
                    progress < 100 || !cardsReady[2] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='dashboard_card_header_container'>
                                <span className='dashboard_card_title'>{showAllTopHigher ? 'Todos los' : 'Top 5'} vehículos con mayor excesos de velocidad</span>
                                <button className='dashboard_card_view_more' onClick={handleShowAllTopHigherToggle}>
                                    {showAllTopHigher ? 'Ver menos' : 'Ver todos'}
                                </button>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateTopHigherRange} setDateRange={setDateTopHigherRange} />
                                    <div className='increment_input_container'>
                                        <label htmlFor="speed_input">Exceso de Velocidad (km/h): </label>
                                        <input
                                            id="speed_input3"
                                            type="number"
                                            value={speedLimit}
                                            min="40"
                                            max="250"
                                            step="5"
                                            onChange={handleChangeSpeedLimit}
                                        />
                                    </div>
                                    <TooltipMui title={duplicateHigherTopCard ? 'Ocultar duplicado' : 'Duplicar tarjeta'} arrow slotProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: '0.8rem',
                                                backgroundColor: '#333',
                                                color: '#fff',
                                                padding: '8px 15px',
                                                borderRadius: '8px',
                                            }
                                        }
                                    }}>
                                        <button className='duplicate_card_btn' onClick={() => { setDuplicateHigherTopCard(!duplicateHigherTopCard) }} >
                                            <span className="material-symbols-outlined">{duplicateHigherTopCard ? 'remove' : 'add'}</span>
                                        </button>
                                    </TooltipMui>
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                    <div className='top_items_header_container'>
                                        <span>Periodo</span>
                                        <span>Actual<br/><span>({formatDateRange(dateTopHigherRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                        <span>Previo<br/><span>({formatDateRange(dateTopHigherRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                    </div>
                                    {
                                        displayedHigherData.length === 0 ?
                                            <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                            :
                                            displayedHigherData.map((item, index) => (
                                                <ItemList
                                                    key={index}
                                                    index={index + 1}
                                                    type={'speedViolation'}
                                                    name={item.car} // Nombre del vehículo
                                                    data={{
                                                        currentViolations: item.currentViolations,
                                                        previousViolations: item.previousViolations
                                                    }}

                                                />
                                            ))
                                    }
                                </div>
                            </div>
                        </>
                    )}
            </div>
            {
                duplicateHigherTopCard ? (
                    <div className='dashboard_card card_top card_short'>
                        {error ?
                            <RetryReport fetchReport={fetchReport} sectionNum={2} />
                            :
                            progress < 100 || !cardsReady[3] ? (
                                <Loader progress={progress} />
                            ) : (
                                <>
                                    <div className='dashboard_card_header_container'>
                                        <span className='dashboard_card_title'>{showAllTopHigher ? 'Todos los' : 'Top 5'} vehículos con mayor excesos de velocidad <span className='text_duplicated'>(duplicada)</span></span>
                                        <button className='dashboard_card_view_more' onClick={handleShowAllTopHigherToggle}>
                                            {showAllTopHigher ? 'Ver menos' : 'Ver todos'}
                                        </button>
                                        <div className='card_config_container'>
                                            <DateRangeSelector dateRange={dateTopHigherRange} setDateRange={setDateTopHigherRange} />
                                            <div className='increment_input_container'>
                                                <label htmlFor="speed_input">Exceso de Velocidad (km/h): </label>
                                                <input
                                                    id="speed_input4"
                                                    type="number"
                                                    value={duplicatedSpeedLimit}
                                                    min="40"
                                                    max="250"
                                                    step="5"
                                                    onChange={handleDuplicatedChangeSpeedLimit}
                                                />
                                            </div>
                                            <TooltipMui title={duplicateHigherTopCard ? 'Ocultar duplicado' : 'Duplicar tarjeta'} arrow slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: '0.8rem',
                                                        backgroundColor: '#333',
                                                        color: '#fff',
                                                        padding: '8px 15px',
                                                        borderRadius: '8px',
                                                    }
                                                }
                                            }}>
                                                <button className='duplicate_card_btn' onClick={() => { setDuplicateHigherTopCard(!duplicateHigherTopCard) }}>
                                                    <span className="material-symbols-outlined">{duplicateHigherTopCard ? 'remove' : 'add'}</span>
                                                </button>
                                            </TooltipMui>
                                        </div>
                                    </div>
                                    <div className='dashboard_card_content_container'>
                                        <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                            <div className='top_items_header_container'>
                                                <span>Periodo</span>
                                                <span>Actual<br/><span>({formatDateRange(dateTopHigherRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                                <span>Previo<br/><span>({formatDateRange(dateTopHigherRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                            </div>
                                            {
                                                duplicatedDisplayedHigherData.length === 0 ?
                                                    <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                                    :
                                                    duplicatedDisplayedHigherData.map((item, index) => (
                                                        <ItemList
                                                            key={index}
                                                            index={index + 1}
                                                            type={'speedViolation'}
                                                            name={item.car} // Nombre del vehículo
                                                            data={{
                                                                currentViolations: item.currentViolations,
                                                                previousViolations: item.previousViolations
                                                            }}

                                                        />
                                                    ))
                                            }
                                        </div>
                                    </div>
                                </>
                            )}
                    </div>
                ) : ''
            }
            <div className='dashboard_card card_top card_short'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={2} />
                    :
                    progress < 100 || !cardsReady[4] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='dashboard_card_header_container'>
                                <span className='dashboard_card_title'>{showAllTopLesser ? 'Todos los' : 'Top 5'} vehículos con menor excesos de velocidad</span>
                                <button className='dashboard_card_view_more' onClick={handleShowAllTopLesserToggle}>
                                    {showAllTopLesser ? 'Ver menos' : 'Ver todos'}
                                </button>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateTopLesserRange} setDateRange={setDateTopLesserRange} />
                                    <div className='increment_input_container'>
                                        <label htmlFor="speed_input">Exceso de Velocidad (km/h): </label>
                                        <input
                                            id="speed_input5"
                                            type="number"
                                            value={speedLimit}
                                            min="40"
                                            max="250"
                                            step="5"
                                            onChange={handleChangeSpeedLimit}
                                        />
                                    </div>
                                    <TooltipMui title={duplicateLesserTopCard ? 'Ocultar duplicado' : 'Duplicar tarjeta'} arrow slotProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: '0.8rem',
                                                backgroundColor: '#333',
                                                color: '#fff',
                                                padding: '8px 15px',
                                                borderRadius: '8px',
                                            }
                                        }
                                    }}>
                                        <button className='duplicate_card_btn' onClick={() => { setDuplicateLesserTopCard(!duplicateLesserTopCard) }} >
                                            <span className="material-symbols-outlined">{duplicateLesserTopCard ? 'remove' : 'add'}</span>
                                        </button>
                                    </TooltipMui>
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                    <div className='top_items_header_container'>
                                        <span>Periodo</span>
                                        <span>Actual<br/><span>({formatDateRange(dateTopLesserRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                        <span>Previo<br/><span>({formatDateRange(dateTopLesserRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                    </div>
                                    {
                                        displayedLesserData.length === 0 ?
                                            <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                            :
                                            displayedLesserData.map((item, index) => (
                                                <ItemList
                                                    key={index}
                                                    index={index + 1}
                                                    type={'speedViolation'}
                                                    name={item.car} // Nombre del vehículo
                                                    data={{
                                                        currentViolations: item.currentViolations,
                                                        previousViolations: item.previousViolations
                                                    }}
                                                />
                                            ))
                                    }
                                </div>
                            </div>
                        </>
                    )}
            </div>
            {
                duplicateLesserTopCard ? (
                    <div className='dashboard_card card_top card_short'>
                        {error ?
                            <RetryReport fetchReport={fetchReport} sectionNum={2} />
                            :
                            progress < 100 || !cardsReady[5] ? (
                                <Loader progress={progress} />
                            ) : (
                                <>
                                    <div className='dashboard_card_header_container'>
                                        <span className='dashboard_card_title'>{showAllTopLesser ? 'Todos los' : 'Top 5'} vehículos con menor excesos de velocidad <span className='text_duplicated'>(duplicada)</span></span>
                                        <button className='dashboard_card_view_more' onClick={handleShowAllTopLesserToggle}>
                                            {showAllTopLesser ? 'Ver menos' : 'Ver todos'}
                                        </button>
                                        <div className='card_config_container'>
                                            <DateRangeSelector dateRange={dateTopLesserRange} setDateRange={setDateTopLesserRange} />
                                            <div className='increment_input_container'>
                                                <label htmlFor="speed_input">Exceso de Velocidad (km/h): </label>
                                                <input
                                                    id="speed_input6"
                                                    type="number"
                                                    value={duplicatedSpeedLimit}
                                                    min="40"
                                                    max="250"
                                                    step="5"
                                                    onChange={handleDuplicatedChangeSpeedLimit}
                                                />
                                            </div>
                                            <TooltipMui title={duplicateLesserTopCard ? 'Ocultar duplicado' : 'Duplicar tarjeta'} arrow slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: '0.8rem',
                                                        backgroundColor: '#333',
                                                        color: '#fff',
                                                        padding: '8px 15px',
                                                        borderRadius: '8px',
                                                    }
                                                }
                                            }}>
                                                <button className='duplicate_card_btn' onClick={() => { setDuplicateLesserTopCard(!duplicateLesserTopCard) }}>
                                                    <span className="material-symbols-outlined">{duplicateLesserTopCard ? 'remove' : 'add'}</span>
                                                </button>
                                            </TooltipMui>
                                        </div>
                                    </div>
                                    <div className='dashboard_card_content_container'>
                                        <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                            <div className='top_items_header_container'>
                                                <span>Periodo</span>
                                                <span>Actual<br/><span>({formatDateRange(dateTopLesserRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                                <span>Previo<br/><span>({formatDateRange(dateTopLesserRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                            </div>
                                            {
                                                duplicatedDisplayedLesserData.length === 0 ?
                                                    <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                                    :
                                                    duplicatedDisplayedLesserData.map((item, index) => (
                                                        <ItemList
                                                            key={index}
                                                            index={index + 1}
                                                            type={'speedViolation'}
                                                            name={item.car} // Nombre del vehículo
                                                            data={{
                                                                currentViolations: item.currentViolations,
                                                                previousViolations: item.previousViolations
                                                            }}
                                                        />
                                                    ))
                                            }
                                        </div>
                                    </div>
                                </>
                            )}
                    </div>
                ) : ''
            }
            <div className='dashboard_card card_top_large card_short'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={2} />
                    :
                    progress < 100 || !cardsReady[6] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='card_header_flex'>
                                <div className='dashboard_card_header_container'>
                                    <span className='dashboard_card_title'>{showAllTopMaxSpeed ? 'Todos los' : 'Top 5'} vehículos con mayor velocidad registrada</span>
                                    <button className='dashboard_card_view_more' onClick={handleShowAllTopMaxSpeedToggle}>
                                        {showAllTopMaxSpeed ? 'Ver menos' : 'Ver todos'}
                                    </button>
                                </div>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateTopMaxSpeedRange} setDateRange={setDateTopMaxSpeedRange} />
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                    <div className='top_items_header_container many_items'>
                                        <span>Periodo</span>
                                        <span>Actual<br/><span>({formatDateRange(dateTopMaxSpeedRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                        <span>Previo<br/><span>({formatDateRange(dateTopMaxSpeedRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                    </div>
                                    {
                                        displayedMaxSpeedData.length === 0 ?
                                            <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                            :
                                            displayedMaxSpeedData.map((item, index) => (
                                                <ItemList
                                                    key={index}
                                                    index={index + 1}
                                                    type={'speedViolationMax'}
                                                    name={item.car}
                                                    data={{
                                                        currentData: item.currentData,
                                                        previousData: item.previousData,
                                                    }}
                                                />
                                            ))
                                    }
                                </div>
                            </div>
                        </>
                    )}
            </div>
            {/* <div style={{ height: '200px', overflow: 'auto' }} onClick={(e) => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(e.target);
          selection.removeAllRanges();
          selection.addRange(range);
        }}><pre>{JSON.stringify(dataReport, 0, 2)}</pre></div> */}
        </div>
    );
};

export default SpeedViolation
